/**
 * Line icons for the marketing page, drawn to one specification: a 24 unit
 * box, 1.7 unit round-capped strokes, no fills. They are set in currentColor
 * so a tile can tint the whole icon by setting a text colour.
 *
 * These are deliberately separate from the product's own iconography. The
 * marketing page is the only screen in the app that is not a record.
 */

interface IconProps {
  className?: string;
}

function Svg({ className, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      {children}
    </svg>
  );
}

/** The mark. Four toes and a pad, the only logo the platform has. */
export function Paw({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <ellipse cx="7.1" cy="8.2" rx="2.5" ry="3.1" transform="rotate(-16 7.1 8.2)" />
      <ellipse cx="12" cy="6.4" rx="2.4" ry="3.2" />
      <ellipse cx="16.9" cy="8.2" rx="2.5" ry="3.1" transform="rotate(16 16.9 8.2)" />
      <ellipse cx="20.3" cy="13.4" rx="2.2" ry="2.6" transform="rotate(24 20.3 13.4)" />
      <path d="M12 12.4c3.1 0 5.6 2.1 5.6 4.7 0 2.2-1.7 3.6-3.9 3.6-1 0-1.3-.4-1.7-.4s-.7.4-1.7.4c-2.2 0-3.9-1.4-3.9-3.6 0-2.6 2.5-4.7 5.6-4.7Z" />
    </svg>
  );
}

/** Paw outline, for the tile version where a solid mark would be too heavy. */
export function PawOutline({ className }: IconProps) {
  return (
    <Svg className={className}>
      <ellipse cx="7.4" cy="8.6" rx="2" ry="2.5" />
      <ellipse cx="12" cy="7.2" rx="2" ry="2.6" />
      <ellipse cx="16.6" cy="8.6" rx="2" ry="2.5" />
      <path d="M12 12.8c2.7 0 4.9 1.8 4.9 4.1 0 1.9-1.5 3.1-3.4 3.1-.9 0-1.1-.3-1.5-.3s-.6.3-1.5.3c-1.9 0-3.4-1.2-3.4-3.1 0-2.3 2.2-4.1 4.9-4.1Z" />
    </Svg>
  );
}

export function CalendarIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <rect x="3.2" y="5" width="17.6" height="15.8" rx="2.6" />
      <path d="M3.2 9.6h17.6M8.2 3.2v3.6M15.8 3.2v3.6" />
      <path d="M7.6 13.4h2.2M14.2 13.4h2.2M7.6 17h2.2M14.2 17h2.2" />
    </Svg>
  );
}

export function CardIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <rect x="2.6" y="5.4" width="18.8" height="13.2" rx="2.6" />
      <path d="M2.6 9.8h18.8M6.4 14.6h3.4" />
    </Svg>
  );
}

export function ChartIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M4.6 19.4V13M9.6 19.4V6.6M14.6 19.4v-8.2M19.4 19.4V9" />
    </Svg>
  );
}

export function SitterIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <circle cx="9" cy="6.6" r="3" />
      <path d="M3.4 20.2c0-3.1 2.5-5.6 5.6-5.6s5.6 2.5 5.6 5.6" />
      <path d="M17.4 12.6c1.6 0 2.9 1.3 2.9 2.9v1.1c0 1.6-1.3 2.9-2.9 2.9" />
      <path d="M17.4 12.6c-.8 0-1.5.4-1.9 1" />
    </Svg>
  );
}

export function DogIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M3.2 10.4V7.2l2.4 1.6h2.8" />
      <path d="M3.2 10.4c0 1.6 1.3 2.9 2.9 2.9h9.2c1.4 0 2.6.9 3 2.2l.9 2.7" />
      <path d="M6.6 13.3v5.9M15.2 13.3v5.9M11 13.3v5.9" />
      <path d="M20.8 8.6c0 1-.8 1.8-1.8 1.8h-2.2" />
    </Svg>
  );
}

export function ScissorsIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <circle cx="6.4" cy="17.6" r="2.6" />
      <circle cx="6.4" cy="6.4" r="2.6" />
      <path d="M8.6 8.2 20 18.4M8.6 15.8 20 5.6" />
    </Svg>
  );
}

export function HomeIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M3.6 10.4 12 3.6l8.4 6.8v8.4a1.8 1.8 0 0 1-1.8 1.8H5.4a1.8 1.8 0 0 1-1.8-1.8Z" />
      <path d="M9.4 20.6v-6.4h5.2v6.4" />
    </Svg>
  );
}

export function CapIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M12 4.2 22 9l-10 4.8L2 9Z" />
      <path d="M6.4 11.2v4.6c0 1.7 2.5 3 5.6 3s5.6-1.3 5.6-3v-4.6" />
    </Svg>
  );
}

export function ClockIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M12 7.2V12l3.2 2" />
    </Svg>
  );
}

export function ChatIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M20.4 12.4c0 3.9-3.8 7-8.4 7-1 0-2-.15-2.9-.42l-4.5 1.42 1.3-3.7c-1.4-1.2-2.3-2.9-2.3-4.7 0-3.9 3.8-7 8.4-7s8.4 3.1 8.4 7Z" />
    </Svg>
  );
}

export function ShieldIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M12 3.2 19.4 6v5.8c0 4-3.1 7.4-7.4 9-4.3-1.6-7.4-5-7.4-9V6Z" />
      <path d="m9 12 2.2 2.2L15.4 10" />
    </Svg>
  );
}

export function BellIcon({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M18 9.6a6 6 0 0 0-12 0c0 5.2-2 6.6-2 6.6h16s-2-1.4-2-6.6Z" />
      <path d="M13.7 19.4a2 2 0 0 1-3.4 0" />
    </Svg>
  );
}

export function ArrowRight({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="M4.8 12h14M13.4 6.4 19 12l-5.6 5.6" />
    </Svg>
  );
}

export function ChevronDown({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="m6.4 9.4 5.6 5.4 5.6-5.4" />
    </Svg>
  );
}

export function PlayCircle({ className }: IconProps) {
  return (
    <Svg className={className}>
      <circle cx="12" cy="12" r="8.8" />
      <path d="m10.2 8.8 5.4 3.2-5.4 3.2Z" />
    </Svg>
  );
}

export function Check({ className }: IconProps) {
  return (
    <Svg className={className}>
      <path d="m5.4 12.6 4.2 4.2L18.6 7.4" />
    </Svg>
  );
}

export function Sparkle({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M12 2.6c.6 4.3 2.5 6.2 6.8 6.8-4.3.6-6.2 2.5-6.8 6.8-.6-4.3-2.5-6.2-6.8-6.8 4.3-.6 6.2-2.5 6.8-6.8Z" />
      <path d="M18.4 15.2c.3 2.2 1.3 3.2 3.5 3.5-2.2.3-3.2 1.3-3.5 3.5-.3-2.2-1.3-3.2-3.5-3.5 2.2-.3 3.2-1.3 3.5-3.5Z" />
    </svg>
  );
}

export function Star({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="m12 2.8 2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.6l-5.8 3.1 1.1-6.5L2.6 9.6l6.5-.9Z" />
    </svg>
  );
}
